import React, { useCallback, useEffect, useMemo } from 'react'
import { Form } from 'ui-kit-sber'
import { Message, useForm } from 'react-hook-form'
import { v4 } from 'uuid'

import { UseFormMethods } from 'react-hook-form/dist/types'
import { SubmitErrorHandler, SubmitHandler, UnpackNestedValue } from 'react-hook-form/dist/types/form'
import { DeepPartial } from 'react-hook-form/dist/types/utils'
import getValidateMessageInstance from '@utils/validation/getValidateMessageInstance'
import getValidateStatusInstance from '@utils/validation/getValidateStatusInstance'
import Caster, { TCasterCastRules } from '@utils/form/Caster'
import toArray from '@utils/array/toArray'
import { TValidatorRules } from '@utils/validation/Validator/Validator'
import { scrollIntoView } from '@utils/scroll/scrollIntoView'
import CFormContext from './CFormContext'

import { ICFormSectionProps } from './CFormSection'
import { ICFormIncludeProps } from './CFormInclude'
import { ICFormItemProps } from './CFormItem'

export type CFormAllowedChilds = ICFormSectionProps | ICFormIncludeProps | ICFormItemProps
export type CFormChildren = React.ReactElement<CFormAllowedChilds>[] | React.ReactElement<CFormAllowedChilds>

export type CFormErrors<TFieldValues> = {
  [key in keyof TFieldValues]?: Message
}

export interface IFormConstructorProps<TFieldValues = any> {
  casts?: TCasterCastRules<TFieldValues>
  children?: CFormChildren
  errors?: CFormErrors<TFieldValues>
  defaultValues?: UnpackNestedValue<DeepPartial<TFieldValues>>
  rules?: TValidatorRules<TFieldValues>
  onSubmit?: SubmitHandler<TFieldValues>
  onSubmitInvalid?: SubmitErrorHandler<TFieldValues>
  useFormState?: UseFormMethods<TFieldValues>
}

const CForm: <TFieldValues>(props: React.PropsWithChildren<IFormConstructorProps<TFieldValues>>) => JSX.Element = ({
  casts = {},
  rules,
  defaultValues: propDefaultValues,
  errors: defaultErrors,
  children,
  onSubmit,
  onSubmitInvalid,
  useFormState: propUseFormState,
}) => {
  const useFormState =
    propUseFormState ??
    useForm({
      mode: 'onChange',
      shouldFocusError: true,
      defaultValues: propDefaultValues,
    })
  const { control, clearErrors, errors, handleSubmit, reset, setError, setValue, getValues, trigger, watch } =
    useFormState

  const formId = useMemo(() => v4(), [])
  const mess = getValidateMessageInstance(errors as any)
  const status = getValidateStatusInstance(errors as any)
  const childs = toArray(children)
  const dummyFn = (_: any) => _

  const updateErrors = useCallback(() => {
    if (defaultErrors && Object.keys(defaultErrors).length > 0) {
      for (const name in defaultErrors) {
        const existingKeys = Object.keys(getValues())
        const message = defaultErrors[name]

        existingKeys.includes(name) &&
          setError(name as any, {
            type: 'manual',
            message,
          })
      }
    } else {
      clearErrors()
    }
  }, [setError, getValues, defaultErrors, clearErrors])

  useEffect(() => {
    updateErrors()
  }, [updateErrors])

  useEffect(() => {
    if (errors) {
      const keys = Object.keys(errors) as Array<keyof typeof errors>
      const first = keys[0]
      const elem = first ? document.querySelector(`[name=${first}]`) : null
      if (elem) scrollIntoView({ child: elem })
    }
  }, [errors, defaultErrors])

  const onValidSubmit: SubmitHandler<any> = (values, event) => {
    if (!onSubmit) return dummyFn
    const castsExists = casts && Object.keys(casts).length > 0
    onSubmit(castsExists ? Caster.cast(values, casts) : values, event)
  }

  return (
    <Form onFinish={handleSubmit(onValidSubmit, onSubmitInvalid)}>
      <CFormContext.Provider
        value={{
          control,
          mess,
          status,
          rules: rules ?? {},
          reset,
          setError,
          setValue,
          getValues,
          clearErrors,
          trigger,
          watch,
          formId,
        }}
      >
        {childs.map((child) => child)}
      </CFormContext.Provider>
    </Form>
  )
}

export default CForm
